import transparentBackground from "@/assets/images/transparent.jpg";
import VoiceSettingsModal from "@/components/modal/VoiceSettingsModal";
import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useWordVocabularyHandlers } from "@/lib/hooks/slides/editor/handlers/word/useWordVocabularyHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useLocalVideoUpload } from "@/lib/hooks/slides/editor/useLocalVideoUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import {
  AIGradingModeEnum,
  type AnswerMode,
  MediaUsageContextSchema,
  type WordVocabularySlideData,
} from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { DEFAULT_VOICE, resolveAnswerModes } from "@ai-teach/utils";
import { type MouseEvent, useRef, useState } from "react";
import toast from "react-hot-toast";
import { AiFillPicture } from "react-icons/ai";
import {
  FaMagic,
  FaMicrophone,
  FaMusic,
  FaPause,
  FaPencilAlt,
  FaPlay,
  FaVideo,
} from "react-icons/fa";
import { HiInformationCircle, HiX } from "react-icons/hi";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import { useSlideEditorContext } from "../../../SlideEditorContext";
import { EditorContainer } from "../../EditorContainer";
import { GradingModeInfoModal } from "../../grammar/response/GradingModeInfoModal";

const GRADING_MODES: {
  value: WordVocabularySlideData["gradingMode"];
  label: string;
  description: string;
  activeClass: string;
}[] = [
  {
    value: AIGradingModeEnum.Values.strictMode,
    label: "Strict",
    description: "Student must use the exact keyword from the expected answer.",
    activeClass: "bg-blue-500 text-white border-blue-500",
  },
  {
    value: AIGradingModeEnum.Values.contextMode,
    label: "Context",
    description: "Any contextually valid answer scores well. No expected answer needed.",
    activeClass: "bg-emerald-500 text-white border-emerald-500",
  },
  {
    value: AIGradingModeEnum.Values.grammarMode,
    label: "Grammar",
    description: "Student must follow the grammatical structure of the expected answer.",
    activeClass: "bg-purple-500 text-white border-purple-500",
  },
];

const ANSWER_MODES: {
  value: AnswerMode;
  label: string;
  description: string;
  icon: typeof FaMicrophone;
}[] = [
  {
    value: "mic",
    label: "Speak",
    description: "Answers out loud with the mic.",
    icon: FaMicrophone,
  },
  {
    value: "write",
    label: "Write",
    description: "Types the answer, or photographs their handwriting.",
    icon: FaPencilAlt,
  },
];

const CARD_MEDIA_TYPES: { value: WordVocabularySlideData["mediaType"]; label: string }[] = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
];

export default function WordVocabularyEditor() {
  const [playingIndex, setPlayingIndex] = useState<"word" | null>(null);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioText, setAudioText] = useState("");
  const [isGenerateAudioModalOpen, setIsGenerateAudioModalOpen] = useState(false);
  const [voiceSettingsText, setVoiceSettingsText] = useState<string | null>(null);

  const wordAudioRef = useRef<HTMLAudioElement | null>(null);

  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const wordVocabularyHandlers = useWordVocabularyHandlers(slide ?? null);

  const { handleFileDrop: handleImageFileDrop, isUploading: isUploadingImage } =
    useLocalImageUpload((media) => wordVocabularyHandlers.addImage?.(media));
  const { handleFileDrop: handleVideoFileDrop, isUploading: isUploadingVideo } =
    useLocalVideoUpload((media) => wordVocabularyHandlers.addVideo?.(media));
  const { handleFileDrop: handleAudioFileDrop } = useLocalAudioUpload((media) =>
    wordVocabularyHandlers.addAudio?.(media)
  );

  const { setActivePanel } = useSlideEditorContext();

  const slideData = slide?.slideData as WordVocabularySlideData | undefined;
  const {
    image,
    video,
    audio,
    correctAnswer,
    instructionText,
    gradingMode,
    recordOnly,
    answerModes,
  } = slideData ?? {
    image: { src: null, id: null, filename: null },
    video: { src: null, id: null, filename: null },
    correctAnswer: "",
    gradingMode: AIGradingModeEnum.Values.strictMode,
  };

  // The card shows either an image or a video, never both.
  const isVideoCard = slideData?.mediaType === "video";

  const slideName = slide?.name ?? "";

  const dispatch = useAppDispatch();

  const update = (patch: Partial<WordVocabularySlideData>) => {
    if (!slide) return;
    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, ...patch } }));
  };

  const activeAnswerModes = resolveAnswerModes(answerModes);

  // An app build predating `answerModes` always shows the mic, so a write-only slide is only
  // write-only on an updated app. Warned about in the UI rather than prevented.
  const toggleAnswerMode = (mode: AnswerMode) => {
    const next = ANSWER_MODES.map((m) => m.value).filter((value) =>
      value === mode ? !activeAnswerModes.includes(mode) : activeAnswerModes.includes(value)
    );

    if (next.length === 0) {
      toast.error("Students need at least one way to answer");
      return;
    }
    update({ answerModes: next });
  };

  const handleRemoveImage = () => {
    update({ image: { id: null, src: null, filename: null } });
  };

  const handleRemoveVideo = () => {
    update({ video: { id: null, src: null, filename: null } });
  };

  const handleRemoveAudio = () => {
    update({ audio: { id: null, src: null, filename: null } });
  };

  const handleAudioPlay = () => {
    const audioEl = wordAudioRef.current;
    if (!audioEl) return;

    if (playingIndex === "word") {
      audioEl.pause();
      audioEl.currentTime = 0;
      setPlayingIndex(null);
      setCurrentTime(0);
      return;
    }

    audioEl.currentTime = 0;
    audioEl.play();
    setPlayingIndex("word");
  };

  const handleAudioSeek = (e: MouseEvent<HTMLElement>) => {
    const audioEl = wordAudioRef.current;
    const dur = audioEl?.duration || duration;
    if (!audioEl || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    if (playingIndex !== "word") handleAudioPlay();
    audioEl.currentTime = ratio * dur;
    setCurrentTime(audioEl.currentTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const openVoiceSettings = () => {
    const text = audioText.trim();
    if (text === "") {
      toast.error("Enter text first to generate audio");
      return;
    }
    setIsGenerateAudioModalOpen(false);
    setVoiceSettingsText(text);
  };

  const RemoveButton = ({
    onClick,
    className = "",
  }: {
    onClick: (e: MouseEvent<HTMLButtonElement>) => void;
    className?: string;
  }) => (
    <button
      type="button"
      aria-label="Remove"
      onClick={onClick}
      className={`shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-white hover:bg-red-600 focus-visible:text-white focus-visible:bg-red-600 transition-colors ${className}`}
    >
      <HiX className="text-sm" />
    </button>
  );

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3 gap-2">
        <h2 className="flex-1 min-w-0 text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slideName || "Listen & Repeat"}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          <label
            className={`flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded-full border transition-colors ${
              recordOnly
                ? "bg-orange-100 border-orange-400 ring-1 ring-orange-300"
                : "bg-transparent border-orange-200 hover:border-orange-300"
            }`}
          >
            <span
              className={`text-[10px] font-bold uppercase tracking-widest ${
                recordOnly ? "text-orange-700" : "text-orange-500"
              }`}
            >
              Record only
            </span>
            <button
              type="button"
              onClick={() => update({ recordOnly: !recordOnly })}
              className={`relative w-8 h-4 flex items-center rounded-full transition-all duration-300 ease-out ring-1 cursor-pointer ${
                recordOnly
                  ? "bg-orange-500 ring-orange-600 justify-end"
                  : "bg-orange-200 ring-orange-300"
              }`}
              aria-pressed={!!recordOnly}
            >
              <span className="w-3 h-3 rounded-full bg-white shadow-sm" />
            </button>
          </label>
          <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
            Listen &amp; Repeat
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col gap-3">
        {/* Correct answer input */}
        <div className="bg-surface rounded-lg p-3 border border-border space-y-2 shrink-0">
          {/* How the student is allowed to answer */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              How can the student answer?
            </span>
            <div className="flex flex-col gap-1">
              {ANSWER_MODES.map((mode) => {
                const isChecked = activeAnswerModes.includes(mode.value);
                const Icon = mode.icon;
                return (
                  <label
                    key={mode.value}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer transition-colors ${
                      isChecked
                        ? "bg-orange-50 border-orange-300 dark:bg-orange-950/20"
                        : "bg-surface border-border hover:border-orange-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleAnswerMode(mode.value)}
                      className="sr-only"
                    />
                    {isChecked ? (
                      <MdCheckBox className="w-4 h-4 shrink-0 text-orange-500" />
                    ) : (
                      <MdCheckBoxOutlineBlank className="w-4 h-4 shrink-0 text-muted-foreground/60" />
                    )}
                    <Icon
                      className={`text-[10px] shrink-0 ${
                        isChecked ? "text-orange-500" : "text-muted-foreground/60"
                      }`}
                    />
                    <span className="text-[11px] font-semibold shrink-0">{mode.label}</span>
                    <span className="text-[9px] text-muted-foreground/70 truncate">
                      {mode.description}
                    </span>
                  </label>
                );
              })}
            </div>
            {!activeAnswerModes.includes("mic") && (
              <p className="text-[9px] text-amber-600 leading-snug">
                Write only. App versions released before writing was added still show the mic here.
              </p>
            )}
          </div>

          {/* Grading mode */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground shrink-0">
                Grading :
              </span>
              <div className="flex gap-1">
                {GRADING_MODES.map((mode) => {
                  const isActive = gradingMode === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => update({ gradingMode: mode.value })}
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${
                        isActive
                          ? mode.activeClass
                          : "bg-surface text-muted-foreground border-border hover:border-gray-400"
                      }`}
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={() => setIsInfoOpen(true)}
                className="text-muted-foreground/50 hover:text-blue-500 transition-colors"
              >
                <HiInformationCircle className="text-lg" />
              </button>
            </div>
            <p className="text-[9px] text-muted-foreground/70 leading-snug">
              {GRADING_MODES.find((m) => m.value === gradingMode)?.description}
            </p>
          </div>

          {gradingMode !== AIGradingModeEnum.Values.contextMode && (
            <>
              <label className="text-[10px] font-semibold text-muted-foreground block">
                {gradingMode === AIGradingModeEnum.Values.grammarMode
                  ? "Grammar Template"
                  : "Correct answer"}
              </label>
              <input
                type="text"
                value={correctAnswer}
                onChange={(e) => update({ correctAnswer: e.target.value })}
                placeholder={
                  gradingMode === AIGradingModeEnum.Values.grammarMode
                    ? "I am going to ..."
                    : "Enter the correct answer..."
                }
                className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </>
          )}
          <label className="text-[10px] font-semibold text-muted-foreground block">Question</label>
          <input
            type="text"
            value={instructionText ?? ""}
            onChange={(e) => update({ instructionText: e.target.value })}
            placeholder="Enter the instruction text..."
            className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
          />
        </div>

        {/* Card media section — an image or a video, chosen by mediaType */}
        <div className="flex-1 min-h-0 rounded-xl border border-border overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-t-lg dark:bg-blue-950/20 border-b border-blue-100 dark:border-blue-900/30 shrink-0">
            <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-widest">
              {isVideoCard ? "Word Video" : "Word Image"}
            </span>
            <div className="flex-1 h-px bg-blue-200/60 dark:bg-blue-800/30" />
            <div className="flex gap-1 shrink-0">
              {CARD_MEDIA_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => update({ mediaType: type.value })}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${
                    slideData?.mediaType === type.value
                      ? "bg-blue-500 text-white border-blue-500"
                      : "bg-surface text-muted-foreground border-border hover:border-blue-400"
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 bg-surface p-2 flex items-center justify-center">
            {slide &&
              (isVideoCard ? (
                <DroppableZone
                  id={`${slide.id}-video`}
                  data={{ id: slide.id, type: "vocabulary-video" }}
                  allowedType={["video"]}
                  className="rounded-lg h-full max-h-[300px] aspect-square"
                  onLocalFileDrop={handleVideoFileDrop}
                  onClick={() => setActivePanel("Media")}
                >
                  <div className="relative h-full w-full flex items-center justify-center rounded overflow-hidden">
                    {isUploadingVideo ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs text-muted-foreground">Uploading…</p>
                      </div>
                    ) : video?.src ? (
                      <>
                        {/* biome-ignore lint/a11y/useMediaCaption: authoring preview of the student-facing card video */}
                        <video
                          src={video.src}
                          controls
                          className="w-full h-full object-contain bg-black"
                        />
                        <RemoveButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveVideo();
                          }}
                          className="absolute top-1.5 right-1.5 z-50 bg-black/30 text-white"
                        />
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center">
                          <FaVideo className="text-muted-foreground text-lg" />
                        </div>
                        <div className="flex flex-col items-center">
                          <h3 className="text-sm font-semibold mb-1">Word Video</h3>
                          <p className="text-xs text-muted-foreground">Drag and drop video here</p>
                        </div>
                      </div>
                    )}
                  </div>
                </DroppableZone>
              ) : (
                <DroppableZone
                  id={`${slide.id}-image`}
                  data={{ id: slide.id, type: "vocabulary-image" }}
                  allowedType={["image"]}
                  className="rounded-lg h-full max-h-[300px] aspect-square"
                  onLocalFileDrop={handleImageFileDrop}
                  onClick={() => setActivePanel("Media")}
                >
                  <div className="relative h-full w-full flex items-center justify-center rounded overflow-hidden">
                    {isUploadingImage ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs text-muted-foreground">Uploading…</p>
                      </div>
                    ) : image.src ? (
                      <>
                        <img
                          src={transparentBackground}
                          alt={image.src}
                          className="w-full h-full object-cover absolute z-0"
                        />
                        <img
                          src={image.src}
                          alt={image.src}
                          className="w-full h-full object-cover z-30"
                        />
                        <RemoveButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveImage();
                          }}
                          className="absolute top-1.5 right-1.5 z-50 bg-black/30 text-white"
                        />
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center">
                          <AiFillPicture className="text-muted-foreground text-lg" />
                        </div>
                        <div className="flex flex-col items-center">
                          <h3 className="text-sm font-semibold mb-1">Word Image</h3>
                          <p className="text-xs text-muted-foreground">Drag and drop image here</p>
                        </div>
                      </div>
                    )}
                  </div>
                </DroppableZone>
              ))}
          </div>
        </div>

        {/* Audio section — a video carries its own sound, so it's hidden for a video card */}
        {!isVideoCard && (
          <div className="shrink-0 rounded-xl border border-border overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/20 border-b border-blue-100 dark:border-blue-900/30 shrink-0">
              <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-widest">
                Word Audio
              </span>
              <div className="flex-1 h-px bg-blue-200/60 dark:bg-blue-800/30" />
              <button
                type="button"
                onClick={() => setIsGenerateAudioModalOpen(true)}
                className="flex items-center gap-1.5 text-[10px] font-semibold text-blue-100 bg-blue-900 hover:bg-blue-700 px-2.5 py-1 rounded-full transition-colors whitespace-nowrap"
              >
                <FaMagic className="text-[9px]" />
                Generate
              </button>
            </div>

            {slide && (
              <DroppableZone
                id={`${slide.id}-audio`}
                data={{ id: slide.id, type: "word-audio" }}
                allowedType={["audio"]}
                className="relative bg-surface p-3"
                onLocalFileDrop={handleAudioFileDrop}
                onClick={() => setActivePanel("Media")}
              >
                {audio?.src && (
                  <audio
                    ref={wordAudioRef}
                    src={audio.src}
                    preload="metadata"
                    onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
                    onLoadedMetadata={(e) => {
                      const d = e.currentTarget.duration;
                      setDuration(d);
                    }}
                    onEnded={() => {
                      setPlayingIndex(null);
                      setCurrentTime(0);
                    }}
                  >
                    <track kind="captions" srcLang="en" default />
                  </audio>
                )}
                {audio?.src ? (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAudioPlay();
                      }}
                      className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 active:scale-95 ${
                        playingIndex === "word"
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-blue-500 hover:bg-blue-600"
                      }`}
                    >
                      {playingIndex === "word" ? (
                        <FaPause className="text-white text-xs ml-0.5" />
                      ) : (
                        <FaPlay className="text-white text-xs ml-0.5" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-foreground truncate">
                        {audio.filename || "Word Audio"}
                      </p>
                      <button
                        type="button"
                        aria-label="Seek"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAudioSeek(e);
                        }}
                        className="mt-1 block w-full h-1.5 bg-muted rounded-full overflow-hidden cursor-pointer"
                      >
                        <div
                          className="h-full bg-blue-500 transition-all duration-100 pointer-events-none"
                          style={{
                            width: duration > 0 ? `${(currentTime / duration) * 100}%` : "0%",
                          }}
                        />
                      </button>
                      <div className="mt-0.5 flex justify-between text-[9px] text-muted-foreground">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(duration || 0)}</span>
                      </div>
                    </div>
                    <RemoveButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveAudio();
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 py-2 text-muted-foreground">
                    <FaMusic className="text-sm" />
                    <p className="text-xs">Drag and drop audio here</p>
                  </div>
                )}
              </DroppableZone>
            )}
          </div>
        )}
      </div>

      <GradingModeInfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />

      {/* TTS MODAL */}
      <Modal
        title="Generate audio"
        onClose={() => setIsGenerateAudioModalOpen(false)}
        isOpen={isGenerateAudioModalOpen}
      >
        <label className="text-[10px] font-semibold text-muted-foreground block">Text</label>
        <input
          type="text"
          value={audioText}
          onChange={(e) => setAudioText(e.target.value)}
          placeholder="Enter text to generate audio..."
          className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none disabled:opacity-60"
        />
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="ghost" onClick={() => setIsGenerateAudioModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={openVoiceSettings} variant="default" disabled={audioText.trim() === ""}>
            Next
          </Button>
        </div>
      </Modal>

      {slide && voiceSettingsText !== null && (
        <VoiceSettingsModal
          audioData={{ id: null, filename: null, src: null }}
          onClose={() => setVoiceSettingsText(null)}
          text={voiceSettingsText}
          slideId={slide.id}
          voice={DEFAULT_VOICE}
          usageContext={MediaUsageContextSchema.Values.aiTextToSpeech}
          onSave={({ src, id, filename }) => {
            wordVocabularyHandlers.addAudio?.(
              { src: src ?? "", id: id ?? "", filename: filename ?? "" },
              slide.id
            );
            setVoiceSettingsText(null);
            setAudioText("");
          }}
        />
      )}
    </EditorContainer>
  );
}
